import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Search, Check, ChevronsUpDown, X, Plus, Radio, Wifi } from 'lucide-react';
import { toast } from 'sonner';

interface Tracker {
    id: number;
    serial_number: string;
    sim_number: string | null;
}

interface TrackerSelectorProps {
    value: number | null;
    onChange: (trackerId: number | null, tracker?: Tracker) => void;
    disabled?: boolean;
    placeholder?: string;
    /** Exclude these tracker IDs from the available list (e.g., already selected for other slot) */
    excludeIds?: number[];
}

export default function TrackerSelector({
    value,
    onChange,
    disabled = false,
    placeholder = 'Select a tracker...',
    excludeIds = [],
}: TrackerSelectorProps) {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateSheet, setShowCreateSheet] = useState(false);

    // New tracker form state
    const [newSerialNumber, setNewSerialNumber] = useState('');
    const [newSimNumber, setNewSimNumber] = useState('');

    // Fetch available trackers (not currently assigned to any vehicle)
    const { data: availableTrackers, isLoading: isLoadingAvailable } = useQuery({
        queryKey: ['available-trackers', searchQuery],
        queryFn: async () => {
            let query = supabase
                .from('gps_trackers')
                .select(`
                    id,
                    serial_number,
                    sim_number,
                    vehicle_trackers(id)
                `)
                .order('serial_number');

            if (searchQuery) {
                query = query.ilike('serial_number', `%${searchQuery}%`);
            }

            const { data, error } = await query;
            if (error) throw error;
            // Filter out already assigned trackers
            return data?.filter(t => !t.vehicle_trackers || t.vehicle_trackers.length === 0) || [];
        },
        staleTime: 30000,
    });

    // Fetch the currently selected tracker (if it's not in available list because it's assigned)
    const { data: currentTracker, isLoading: isLoadingCurrent } = useQuery({
        queryKey: ['tracker', value],
        queryFn: async () => {
            if (!value) return null;
            const { data, error } = await supabase
                .from('gps_trackers')
                .select('id, serial_number, sim_number')
                .eq('id', value)
                .single();
            if (error) throw error;
            return data as Tracker;
        },
        enabled: !!value,
        staleTime: 60000,
    });

    // Create tracker mutation
    const createTrackerMutation = useMutation({
        mutationFn: async () => {
            if (!newSerialNumber.trim()) throw new Error('Serial number is required');

            const { data, error } = await supabase
                .from('gps_trackers')
                .insert({
                    serial_number: newSerialNumber.trim(),
                    sim_number: newSimNumber.trim() || null,
                })
                .select('id, serial_number, sim_number')
                .single();

            if (error) throw error;
            return data as Tracker;
        },
        onSuccess: (tracker) => {
            toast.success('Tracker created');
            queryClient.invalidateQueries({ queryKey: ['available-trackers'] });
            // Auto-select the newly created tracker
            onChange(tracker.id, tracker);
            // Reset form and close sheet
            setNewSerialNumber('');
            setNewSimNumber('');
            setShowCreateSheet(false);
        },
        onError: (e: Error) => toast.error(e.message),
    });

    // Merge available trackers with current tracker (if not already in list)
    const trackers = useMemo(() => {
        const list = (availableTrackers || [])
            .filter(t => !excludeIds.includes(t.id))
            .map(t => ({ id: t.id, serial_number: t.serial_number, sim_number: t.sim_number }));
        if (currentTracker && !list.find(t => t.id === currentTracker.id)) {
            list.unshift(currentTracker);
        }
        return list;
    }, [availableTrackers, currentTracker, excludeIds]);

    const isLoading = isLoadingAvailable || (!!value && isLoadingCurrent);

    // Find the selected tracker for display
    const selectedTracker = trackers?.find((t) => t.id === value) || currentTracker;

    const handleOpenCreateSheet = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setOpen(false);
        setShowCreateSheet(true);
    };

    const TrackerItem = ({ t, isSelected }: { t: Tracker; isSelected?: boolean }) => (
        <button
            type="button"
            className={`w-full text-left cursor-pointer group rounded-lg border p-3 transition-all hover:border-primary hover:bg-primary/5 ${isSelected
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'border-border/60 bg-card'
                }`}
            onClick={() => {
                onChange(t.id, t);
                setOpen(false);
            }}
        >
            <div className="flex justify-between items-start mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400">
                    GPS Tracker
                </span>
                {isSelected && <Check className="h-3.5 w-3.5 text-primary" />}
            </div>

            <div className="mb-1 flex items-center gap-2">
                <Radio className="h-4 w-4 text-primary" />
                <span className="text-lg font-bold tracking-tight">
                    {t.serial_number}
                </span>
            </div>

            <div className="flex items-center justify-between gap-4">
                <p className="text-xs text-muted-foreground truncate flex-1">
                    {t.sim_number ? `SIM: ${t.sim_number}` : "No SIM assigned"}
                </p>
                <code className="text-[10px] text-muted-foreground/50 font-mono">
                    #{t.id}
                </code>
            </div>
        </button>
    );

    // Filter out selected from list
    const otherTrackers = trackers.filter(t => t.id !== value && !excludeIds.includes(t.id));

    return (
        <>
            {/* Tracker Selection Sheet */}
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 h-[calc(100vh-2rem)] !w-[500px] !max-w-none rounded-2xl p-0 flex flex-col gap-0 border shadow-2xl bg-background data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right duration-300 focus:outline-none"
                >
                    <SheetHeader className="p-4 border-b">
                        <SheetTitle>Select Tracker</SheetTitle>
                    </SheetHeader>

                    {/* Search input */}
                    <div className="p-4 border-b bg-muted/10">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search by serial number..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 bg-background"
                            />
                        </div>
                    </div>

                    <ScrollArea className="flex-1">
                        <div className="p-4 space-y-4">
                            {/* Clear selection button */}
                            {selectedTracker && (
                                <button
                                    type="button"
                                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-accent cursor-pointer text-muted-foreground rounded-md transition-colors"
                                    onClick={() => {
                                        onChange(null);
                                        setOpen(false);
                                    }}
                                >
                                    <X className="h-4 w-4 shrink-0" />
                                    <span className="text-sm">Clear selection</span>
                                </button>
                            )}

                            {/* Selected item sticky at top */}
                            {selectedTracker && (
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Currently Selected</Label>
                                    <TrackerItem t={selectedTracker} isSelected />
                                </div>
                            )}

                            {/* Available Trackers */}
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Available Trackers</Label>
                                {isLoadingAvailable ? (
                                    <div className="flex flex-col gap-3">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="h-24 rounded-lg bg-muted/40 animate-pulse border border-border/40" />
                                        ))}
                                    </div>
                                ) : !otherTrackers?.length ? (
                                    <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed rounded-lg bg-muted/10">
                                        <div className="p-3 rounded-full bg-muted/20 mb-3">
                                            <Search className="h-6 w-6 text-muted-foreground" />
                                        </div>
                                        <p className="text-sm font-medium">No trackers found</p>
                                        <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
                                            {searchQuery ? "Try checking your spelling or using different keywords." : "No unassigned trackers available for selection."}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {otherTrackers.map((t) => (
                                            <TrackerItem key={t.id} t={t} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </ScrollArea>

                    {/* Add new tracker button - fixed at bottom */}
                    <div className="p-4 border-t bg-muted/10">
                        <Button
                            className="w-full gap-2"
                            onClick={handleOpenCreateSheet}
                        >
                            <Plus className="h-4 w-4" />
                            Create New Tracker
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Trigger Button (outside Sheet) */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                disabled={disabled || isLoading}
                className={`w-full text-left cursor-pointer rounded-lg border p-3 transition-all hover:border-primary hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed ${selectedTracker
                    ? 'border-primary/50 bg-primary/5'
                    : 'border-border/60 bg-card'
                    }`}
            >
                {isLoading ? (
                    <div className="flex items-center gap-2 py-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">Loading...</span>
                    </div>
                ) : selectedTracker ? (
                    <>
                        <div className="flex justify-between items-start mb-2">
                            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400">
                                GPS Tracker
                            </span>
                            <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="mb-1 flex items-center gap-2">
                            <Radio className="h-4 w-4 text-primary" />
                            <span className="text-lg font-bold tracking-tight">
                                {selectedTracker.serial_number}
                            </span>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                            <p className="text-xs text-muted-foreground truncate flex-1">
                                {selectedTracker.sim_number ? `SIM: ${selectedTracker.sim_number}` : "No SIM assigned"}
                            </p>
                            <code className="text-[10px] text-muted-foreground/50 font-mono">
                                #{selectedTracker.id}
                            </code>
                        </div>
                    </>
                ) : (
                    <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-muted-foreground">{placeholder}</span>
                        <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                    </div>
                )}
            </button>

            {/* Create Tracker Sheet */}
            <Sheet open={showCreateSheet} onOpenChange={setShowCreateSheet}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !w-[30vw] !max-w-none rounded-2xl flex flex-col shadow-2xl border bg-background"
                >
                    <SheetHeader>
                        <SheetTitle>Create New Tracker</SheetTitle>
                    </SheetHeader>

                    <div className="flex-1 mt-6 space-y-4">
                        <div className="space-y-2">
                            <Label>Serial Number *</Label>
                            <Input
                                value={newSerialNumber}
                                onChange={(e) => setNewSerialNumber(e.target.value)}
                                placeholder="Enter serial number"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>SIM Number</Label>
                            <Input
                                value={newSimNumber}
                                onChange={(e) => setNewSimNumber(e.target.value)}
                                placeholder="Enter SIM number (optional)"
                            />
                        </div>
                    </div>

                    <div className="pt-4 border-t flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowCreateSheet(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => createTrackerMutation.mutate()}
                            disabled={!newSerialNumber.trim() || createTrackerMutation.isPending}
                        >
                            {createTrackerMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Create Tracker
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
